import { logger } from "@origin-digital/reporting-client";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { IFetchAccessToken } from "@origin-digital/event-dispatcher";

import { getAuth0Config } from "../../config";
import { IAuth0Config } from "../../interfaces";
import { decodeJwt, verifyJwt } from "../jwtUtils";
import { getCookie } from "../utils";
import { LOG_TAG } from "../../logTag";
import { requiresInteractiveLogin } from "../const";
import { getCachedJwtEntry } from "./cacheUtils";
import { OriginSpaAuthClient } from "./OriginSpaAuthClient";

const SAP_TOKEN_COOKIE = "user-token";
const SAP_TIMEOUT_COOKIE = "is-logged-in";

// jwtUtils.ts's assertClaimsAreValid() throws this exact message when a
// cached JWT's `exp` claim has simply passed — the overwhelmingly common,
// expected reason verification fails (time passed since the visitor's last
// login, same idea as login_required above), not a bug. Other throw paths in
// that file (decode failure, algorithm/audience/issuer mismatch, actual
// signature verification failure) suggest a real bug or a tampered token and
// should stay at `error`.
const isExpectedJwtExpiry = (error: unknown): boolean =>
  error instanceof Error && error.message.includes("the token is expired");

const handleFailedSilentAuth =
  (auth0Params: IAuth0Config) => async (e: Error) => {
    // These are temporary to help identify/debug the root cause of failed silent auth
    const hasCookie = !!getCookie(SAP_TOKEN_COOKIE);
    const sapTokenTimeout = getCookie(SAP_TIMEOUT_COOKIE);
    const isSapTokenExpired = sapTokenTimeout
      ? Date.now() > parseInt(sapTokenTimeout, 10)
      : true;

    const cachedJwt = getCachedJwtEntry(auth0Params);
    let isJwtValid = !!cachedJwt;

    try {
      if (cachedJwt) {
        const decoded = decodeJwt(cachedJwt.body.access_token);
        await verifyJwt(cachedJwt.body.access_token, decoded, auth0Params);
      }
    } catch (error) {
      isJwtValid = false;
      const logInvalidJwt = isExpectedJwtExpiry(error)
        ? logger.debug
        : logger.error;
      logInvalidJwt(`${LOG_TAG} Invalid JWT token`, error);
    }

    const expectedUnauth = requiresInteractiveLogin(e);
    const logSilentAuthFailure = expectedUnauth ? logger.debug : logger.error;

    // Deliberately not `...e`: reporting-client's fireLogging() has a second,
    // unconditional `if (data.error) console.error(data.error)` side-effect
    // that ignores the chosen log level entirely — spreading Auth0's error
    // (which has an `.error` code, e.g. "login_required") straight into the
    // data payload would always print a bare, unprefixed red console.error
    // regardless of whether this call is logger.debug or logger.error,
    // undermining the classification above. Pick the same fields out under
    // renamed keys instead — same diagnostic info, no `.error` key to trip it.
    const auth0Error = e as {
      error?: string;
      error_description?: string;
      state?: string;
    };
    logSilentAuthFailure(`${LOG_TAG} Error getting jwt silently`, {
      errorCode: auth0Error.error,
      errorDescription: auth0Error.error_description,
      state: auth0Error.state,
      hasCookie,
      isSapTokenExpired,
      hasJwt: !!cachedJwt,
      isJwtValid,
    });

    // login_required/interaction_required (see requiresInteractiveLogin in
    // ../const, shared with OriginWebAuthClient's checkSession() handling)
    // are now classified above as expected and logged at `debug` instead of
    // `error`. Redirecting to login on this path is a separate, bigger UX
    // decision — not done here. SY-1629
    //
    // `expectedUnauth` is carried on the rejected error so downstream
    // consumers of getJwt() (e.g. @od/chat's getUserInfo) can make the same
    // "expected vs genuine failure" distinction instead of re-logging every
    // failure at `error` themselves.
    return Promise.reject(
      Object.assign(new Error("error getting jwt silently"), {
        expectedUnauth,
      })
    );
  };

/**
 * Triggers Auth0 step-up by calling `loginWithRedirect` with `stepup=true`.
 * The tenant's `stepup=true` Auth0 Rule responds by requiring MFA and
 * granting elevated scopes; the redirect returns to `returnTo` with the
 * exchange code.
 *
 * No-ops if `additionalScopes` is empty.
 *
 * Used by `@origin-digital/mesh`'s `WebPlatformHandler.STEP_UP_AUTH` topic
 * to serve desktop-web microsites. Native microsites use `StepUpGuard`
 * from this package's `react/` subfolder which calls `loginWithRedirect`
 * directly.
 */
export const stepUpAuth = async (
  env: EnvironmentNames,
  returnTo: string,
  additionalScopes: string[]
): Promise<void> => {
  if (additionalScopes.length === 0) return;
  const auth0Params = getAuth0Config(env);
  // eslint-disable-next-line no-new
  const auth0 = new OriginSpaAuthClient(auth0Params);
  await auth0.loginWithRedirect({
    redirectUri: returnTo,
    additionalParams: { stepup: "true" },
  });
};

/**
 * Silent JWT retrieval. Wraps `OriginSpaAuthClient.getJwtSilently()` with
 * structured failure logging (cookie state, JWT cache validity) so
 * recurring silent-auth failures can be diagnosed in production.
 *
 * Web-only — native microsites get their JWT via mesh's native channel
 * (`fetchAccessToken` → mobile shell → Keychain).
 */
export const getJwt = (env: EnvironmentNames): IFetchAccessToken["result"] => {
  const auth0Params = getAuth0Config(env);
  if (!auth0Params) {
    return Promise.reject(
      new Error(`auth0 config missing for environment: ${env}`)
    );
  }
  const originSpaAuthClient = new OriginSpaAuthClient(auth0Params);

  return originSpaAuthClient
    .getJwtSilently()
    .then((accessToken) => ({ accessToken }))
    .catch(handleFailedSilentAuth(auth0Params));
};
